import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditCustomDeadlineModal from '@modals/CreateOrEditCustomDeadlineModal'

import type { CustomDeadlineModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateOrEditCustomDeadlineModal: () => void,
  },
}

const defaultState = {
  showCreateOrEditCustomDeadlineModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditCustomDeadlineModal: {
      closeModal: () => setState({ showCreateOrEditCustomDeadlineModal: false }),
      createCustomDeadline: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCustomDeadline: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditCustomDeadlineModal } = state

  const filtersPayload = useFilters({ persistFilters: true })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditCustomDeadlineModal: (payload: { customDeadline: CustomDeadlineModel }) => {
        setModalData('CreateOrEditCustomDeadlineModal', payload)
        setState({ showCreateOrEditCustomDeadlineModal: true })
      },
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditCustomDeadlineModal
          callbacks={callbacks('CreateOrEditCustomDeadlineModal', setState)}
          showModal={showCreateOrEditCustomDeadlineModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
